import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, GitCompare, FileText, BarChart3, Search, Lightbulb, Info, ChevronRight } from "lucide-react";
import { ResumeJobMatchSearch } from "@/components/ResumeJobMatchSearch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const ResumeJobMatchReport = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-green-50/50 p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              onClick={() => navigate('/jobintel-360-reports')}
              className="bg-white"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-lime-500 to-green-500 flex items-center justify-center shadow-lg">
              <GitCompare className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Resume + Job Match Analysis</h1>
              <p className="text-muted-foreground">See how well your resume matches target job postings</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            onClick={() => navigate('/jobintel-360-reports')}
            className="bg-white"
          >
            <FileText className="h-4 w-4 mr-2" />
            My Reports
          </Button>
        </div>

        {/* Hero Card */}
        <Card className="border-2 border-green-400 bg-green-50 shadow-lg">
          <CardHeader className="pb-4">
            <CardTitle className="text-2xl font-bold">
              Know Your Match Score Before You Apply
            </CardTitle>
            <CardDescription className="text-base leading-relaxed">
              Upload your resume and a job description to get an instant 
              <span className="font-semibold text-green-700"> match percentage</span>, 
              <span className="font-semibold text-green-700"> gap analysis</span>, and 
              <span className="font-semibold text-green-700"> actionable recommendations</span> — 
              so you can tailor your application for maximum impact.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Info Dialogs Row */}
            <div className="flex items-center gap-6">
              <Dialog>
                <DialogTrigger asChild>
                  <button className="flex items-center gap-2 text-green-700 hover:text-green-800 font-medium transition-colors">
                    <Info className="w-4 h-4" />
                    Why you need this
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Why You Need Resume Match Analysis</DialogTitle>
                    <DialogDescription className="space-y-3 pt-4">
                      <p>ATS systems scan resumes for keyword matches before a human ever sees your application.</p>
                      <p><strong>The problem:</strong> Most applicants submit the same generic resume to every job, resulting in low match scores and rejection.</p>
                      <p><strong>The solution:</strong> See exactly where your resume matches (and misses) key requirements, so you can optimize before applying.</p>
                    </DialogDescription>
                  </DialogHeader>
                </DialogContent>
              </Dialog>

              <Dialog>
                <DialogTrigger asChild>
                  <button className="flex items-center gap-2 text-green-700 hover:text-green-800 font-medium transition-colors">
                    What you'll receive
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>What You'll Receive</DialogTitle>
                    <DialogDescription className="space-y-3 pt-4">
                      <ul className="list-disc list-inside space-y-2">
                        <li><strong>Overall Match Score:</strong> Percentage rating of resume-to-job fit</li>
                        <li><strong>Keyword Analysis:</strong> Missing and matching keywords</li>
                        <li><strong>Skills Gap:</strong> Required skills you need to highlight or add</li>
                        <li><strong>Experience Alignment:</strong> How your experience maps to requirements</li>
                        <li><strong>Improvement Tips:</strong> Specific edits to boost your match score</li>
                      </ul>
                    </DialogDescription>
                  </DialogHeader>
                </DialogContent>
              </Dialog>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white border-2 border-green-600 rounded-lg p-4 text-center shadow-sm">
                <GitCompare className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-green-700">Side-by-Side</div>
                <div className="text-sm text-muted-foreground">Comparison</div>
              </div>
              <div className="bg-white border-2 border-green-600 rounded-lg p-4 text-center shadow-sm">
                <BarChart3 className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-green-700">Match %</div>
                <div className="text-sm text-muted-foreground">Score Rating</div>
              </div>
              <div className="bg-white border-2 border-green-600 rounded-lg p-4 text-center shadow-sm">
                <Search className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-green-700">Gap</div>
                <div className="text-sm text-muted-foreground">Analysis</div>
              </div>
              <div className="bg-white border-2 border-green-600 rounded-lg p-4 text-center shadow-sm">
                <Lightbulb className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-green-700">Tips</div>
                <div className="text-sm text-muted-foreground">Actionable</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Search Form */}
        <ResumeJobMatchSearch />
      </div>
    </div>
  );
};

export default ResumeJobMatchReport;
